import React, { useState, useRef, useEffect } from 'react'
import {
  BoxShadow,
  CollapsedRow,
  ColumnContainer,
  Container,
  ContainerAnimation,
  DisplayAnimation,
  IconContainer,
  SpacingRow,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Wrapper,
} from './styles'
import { formatMoney } from '../../utils/format-money'
import { TColumn } from '../../type'
import Button from '../button'
import Icon from '../icon'
import SvgArrow from '../../assets/svg-arrow'
import SvgHand from '../../assets/svg-hand'
import Tooltip from '../tooltip'

type Props = {
  'data-qa'?: string
  animation?: boolean
  background?: 'default' | 'light'
  columns: Column[]
  details?: JSX.Element
  rows: Row[]
  style?: object
  largest?: boolean
}

interface Row extends Object {
  isDetaisOpen?: boolean
}

const Table: React.FC<Props> = (props: Props): JSX.Element => {
  const componentRef = useRef(null)
  const [isScroll, setIsScroll] = useState<boolean>(false)
  const [isScrollEnabled, setIsScrollEnabled] = useState<boolean>(false)
  const [ordenedId, setOrdenedId] = useState<number>(null)
  const [rowSelected, setRowSelected] = useState<number>()

  function handleScroll() {
    if (!isScrollEnabled && props.animation) removeScrollEvent(componentRef)
    const { scrollLeft, offsetWidth, scrollWidth } = componentRef.current
    setIsScroll(offsetWidth + scrollLeft !== scrollWidth)
  }

  function isColumnSelected(key: number) {
    key === ordenedId ? setOrdenedId(null) : setOrdenedId(key)
  }

  useEffect(() => {
    handleScroll()
  }, [])

  useEffect(() => {
    props.rows.find((row: { isDetailsOpen: boolean }, index) => {
      if (row.isDetailsOpen) {
        setRowSelected(index)
        return row
      }
    })
  }, [])

  return (
    <Container>
      {isScroll && <BoxShadow />}
      <Wrapper
        ref={componentRef}
        onScroll={handleScroll}
        onAnimationEnd={() => setIsScrollEnabled(true)}
        onAnimationStart={() => setIsScrollEnabled(false)}
        animation={props.animation}
        style={props.style}
        largest={props.largest}
      >
        {props.animation && (
          <ContainerAnimation>
            <DisplayAnimation>
              <SvgHand />
              <SvgArrow />
            </DisplayAnimation>
          </ContainerAnimation>
        )}
        <TableContainer
          data-qa={props['data-qa'] || `table-${props.background ?? 'default'}`}
        >
          <TableHead>
            <TableRow
              data-qa={props['data-qa'] && `${props['data-qa']}-header-row`}
            >
              {composeColumns(props.columns, props.rows)?.map((column, key) =>
                column.isOrderByColumn ? (
                  <TableCell
                    key={key}
                    style={{ width: column.width, textAlign: column.align }}
                    onClick={() => {
                      column.handleOrderColumn()
                      isColumnSelected(key)
                    }}
                    className={key === ordenedId ? 'is-ordened' : ''}
                    data-qa={
                      props['data-qa'] &&
                      `${props['data-qa']}-header-cell-${key}`
                    }
                  >
                    <ColumnContainer>
                      {column?.label}
                      <Icon name={'keyboard_arrow_down'} />
                    </ColumnContainer>
                  </TableCell>
                ) : (
                  <TableCell
                    key={key}
                    style={{ width: column.width, textAlign: column.align }}
                    data-qa={
                      props['data-qa'] &&
                      `${props['data-qa']}-header-cell-${key}`
                    }
                  >
                    {column?.label}
                  </TableCell>
                ),
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {props.rows?.map((row: any, index: number) => (
              <Row
                columns={composeColumns(props.columns)}
                row={composeRow(row)}
                key={index}
                data-qa={props['data-qa'] && `${props['data-qa']}-row-${index}`}
                disabled={row.disabled}
                background={props.background}
                id={index}
                onSelected={() => setRowSelected(index)}
                rowSelected={rowSelected}
              />
            ))}
          </TableBody>
        </TableContainer>
      </Wrapper>
    </Container>
  )
}

export interface Column {
  align?:
    | 'center'
    | 'end'
    | 'justify'
    | 'left'
    | 'match-parent'
    | 'right'
    | 'start'
  id: string
  isOrderByColumn?: boolean
  handleOrderColumn?: Function
  label: string | JSX.Element
  type?: TColumn
  width?: string | number
}

const cellOptions = (value: any): object => {
  return {
    action: (
      <Tooltip title={value?.label}>
        <Button
          icon={value?.icon}
          onClick={value?.handleEvent}
          disabled={value?.isDisabled}
          theme="tertiary"
        />
      </Tooltip>
    ),
    date_time: new Date(value)?.toLocaleDateString('pt-BR'),
    file: <Tooltip title={value?.toString()}>{value?.toString()}</Tooltip>,
    icon: (
      <Tooltip title={value?.label}>
        <Icon name={value?.name} color={value?.color} />
      </Tooltip>
    ),
    jsx: value,
    money: formatMoney(value, 'pt-BR', 'BRL'),
    number: value?.toString(),
  }
}

interface IRowProps {
  columns: Column[]
  row: any
  disabled?: boolean
  background?: 'default' | 'light'
  onSelected?: () => void
  id?: number
  rowSelected?: number
}

const Row = (rowProps: IRowProps): JSX.Element => {
  const [isSelected, setIsSelected] = useState<boolean>(false)
  const isCollapsable: boolean = Boolean(rowProps.row.details)
  const showDetails: boolean = shouldShowDetails(isSelected, isCollapsable)
  const isRowSelected = () => rowProps.id === rowProps.rowSelected
  const classes: string = composeClasses(
    isCollapsable,
    showDetails,
    rowProps.disabled,
    rowProps.background || 'default',
  )

  useEffect(() => {
    !isRowSelected()
      ? setIsSelected(false)
      : setIsSelected(!isSelected && isCollapsable)
  }, [rowProps.rowSelected])

  return (
    <>
      <TableRow
        onClick={() => {
          rowProps.onSelected()
          isRowSelected() && setIsSelected(!isSelected && isCollapsable)
        }}
        className={classes}
      >
        {rowProps.columns.map((column, columnIndex) => {
          const value = rowProps.row[column.id]

          return (
            <TableCell
              key={columnIndex}
              style={{ maxWidth: column.width, textAlign: column.align }}
            >
              {cellOptions(value)[column.type] || value?.toString()}
            </TableCell>
          )
        })}
      </TableRow>

      {isCollapsable &&
        RowDetails(rowProps.columns, rowProps.row.details, showDetails)}
    </>
  )
}

const RowDetails = (
  columns: Column[],
  details: any,
  isOpen: boolean,
): React.ReactNode => (
  <>
    <CollapsedRow isOpen={isOpen}>
      <TableCell colSpan={columns.length}>{isOpen && details}</TableCell>
    </CollapsedRow>

    <SpacingRow isOpen={isOpen} />
  </>
)

export default Table

Table.defaultProps = {
  largest: false,
}

const composeClasses = (
  isCollapsable: boolean,
  showDetails: boolean,
  disabled: boolean,
  background: string,
): string => {
  return [
    isCollapsable && 'collapsable',
    showDetails && 'is-open',
    disabled && 'disabled',
    background,
  ].join(' ')
}

const shouldShowDetails = (
  isSelected: boolean,
  isCollapsable: boolean,
): boolean => {
  return isSelected && isCollapsable
}

const composeColumns = (columns: any, rows = []) => {
  if (!columns) return
  const haveDetails = rows.some((row) => row.details)
  const EMPTYLABEL = ''
  const WIDTH = haveDetails ? '5rem' : '0rem'
  const column = {
    id: 'showDetails',
    label: EMPTYLABEL,
    type: 'jsx',
    width: WIDTH,
  }

  return [...columns, column]
}

const composeRow = (row: any) => {
  if (!row) return
  const showDetails = row.details ? (
    <IconContainer>
      <Icon name={'keyboard_arrow_down'} />
    </IconContainer>
  ) : (
    <React.Fragment />
  )

  return Object.assign(row, { showDetails })
}

const removeScrollEvent = (ref: React.RefObject<HTMLDivElement>) => {
  const INITIAL_COORDINATE = 0
  ref.current.scrollTo(INITIAL_COORDINATE, INITIAL_COORDINATE)
}
